import { Controller } from "@hotwired/stimulus"
import { announcePopoverOpen, bindExclusivePopover, isPopoverOpen } from "../../utils/exclusive-popover.util"
import { installFilterHotkey } from "../../utils/filter-hotkey.util"
import { isCompactViewport } from "../../utils/pointer.util"
import { cancelPopoverClose, closeWithPopoverMotion } from "../../utils/popover-motion.util"

const ACTIVE_FILTER_QUERY_KEYS = [
  "bank_account_id",
  "date_from",
  "date_to",
  "amount_from",
  "amount_to",
  "matching_status[]",
  "classification_detail[]",
  "loan_id[]"
]

// Connects to data-controller="bank--transaction-filter-menu"
// Handles UI state for the bank transactions filter menu
export default class extends Controller {
  static targets = [
    "activeFilterIndicator",
    "filterButton",
    "filterMenu",
    "filterItem",
    "filterContent",
    "searchClearButton",
    "searchInput",
    "activeStatusBankAccount",
    "activeStatusDate",
    "activeStatusAmount",
    "activeStatusStatus",
    "activeStatusClassification",
    "activeStatusDetail",
    "activeStatusLoan",
    "activeStatusOrder"
  ]

  connect() {
    this.#initializeActiveStatus()
    this.refreshActiveFilterIndicator()
    this.#restoreSearchFromUrl()
    this.boundHandleGlobalPointerDown = this.#handleGlobalPointerDown.bind(this)
    this.boundHandleResize = this.#handleResize.bind(this)
    this.boundRefreshActiveFilterIndicator = this.refreshActiveFilterIndicator.bind(this)
    this.unbindExclusivePopover = bindExclusivePopover(this.element, () => this.closeFilterMenu())
    this.removeFilterHotkey = installFilterHotkey(this)
    document.addEventListener("pointerdown", this.boundHandleGlobalPointerDown, true)
    window.addEventListener("resize", this.boundHandleResize, { passive: true })
    this.filterMenuTarget.addEventListener("change", this.boundRefreshActiveFilterIndicator)
    this.filterMenuTarget.addEventListener("input", this.boundRefreshActiveFilterIndicator)
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.boundHandleGlobalPointerDown, true)
    window.removeEventListener("resize", this.boundHandleResize)
    this.filterMenuTarget.removeEventListener("change", this.boundRefreshActiveFilterIndicator)
    this.filterMenuTarget.removeEventListener("input", this.boundRefreshActiveFilterIndicator)
    this.unbindExclusivePopover?.()
    this.removeFilterHotkey?.()
  }

  #initializeActiveStatus() {
    const urlSearchParams = new URLSearchParams(window.location.search)

    if (this.hasActiveStatusBankAccountTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["bank_account_id"]),
        this.activeStatusBankAccountTarget
      )
    }
    if (this.hasActiveStatusDateTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["date_from", "date_to"]),
        this.activeStatusDateTarget
      )
    }
    if (this.hasActiveStatusAmountTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["amount_from", "amount_to"]),
        this.activeStatusAmountTarget
      )
    }
    if (this.hasActiveStatusStatusTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["matching_status[]"]),
        this.activeStatusStatusTarget
      )
    }
    if (this.hasActiveStatusClassificationTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["matching_status[]"]),
        this.activeStatusClassificationTarget
      )
    }
    if (this.hasActiveStatusDetailTarget) {
      this.toggleActiveStatus(
        this.#toggleChecker(urlSearchParams, ["classification_detail[]"]),
        this.activeStatusDetailTarget
      )
    }
    if (this.hasActiveStatusLoanTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["loan_id[]"]), this.activeStatusLoanTarget)
    }
    if (this.hasActiveStatusOrderTarget) {
      this.toggleActiveStatus(this.#toggleChecker(urlSearchParams, ["order"]), this.activeStatusOrderTarget)
    }
  }

  #toggleChecker(urlSearchParams, keys) {
    if (urlSearchParams.has("quickFilter")) {
      return false
    }

    return keys.some((key) => urlSearchParams.has(key))
  }

  toggleActiveStatus(isActive, target) {
    if (isActive) {
      target.classList.remove("d-none")
    } else {
      target.classList.add("d-none")
    }
  }

  refreshActiveFilterIndicator() {
    if (!this.hasActiveFilterIndicatorTarget) return

    const isActive = this.#hasActiveFilterQueryParams() || this.#hasActiveFilterControls()
    this.activeFilterIndicatorTarget.classList.toggle("d-none", !isActive)
    this.filterButtonTarget.classList.toggle("filter-button--has-active-filters", isActive)
  }

  openFilterMenu(event) {
    event?.preventDefault()
    if (this.#isPopoverOpen()) return
    announcePopoverOpen(this.element)
    this.#applyPopoverViewportSizing()
    cancelPopoverClose(this.filterMenuTarget)
    this.filterMenuTarget.showPopover()
    this.filterButtonTarget.classList.add("filter-button--active")
  }

  closeFilterMenu() {
    if (!this.#isPopoverOpen()) return
    closeWithPopoverMotion(this.filterMenuTarget, () => {
      this.filterMenuTarget.hidePopover()
    })
    this.filterButtonTarget.classList.remove("filter-button--active")
  }

  selectFilter(event) {
    const { currentTarget } = event
    const selectedId = currentTarget.dataset.id

    this.filterItemTargets.forEach((el) => {
      el.classList.toggle("sidebar__tab--active", el === currentTarget)
    })

    this.filterContentTargets.forEach((el) => {
      el.classList.toggle("tab-content--hidden", el.id !== selectedId)
    })
  }

  clearSearch(event) {
    event?.preventDefault()
    if (!this.hasSearchInputTarget) return

    this.searchInputTarget.value = ""
    this.searchInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    // The button that was just activated hides itself the moment the field
    // empties (the CSS keys it on `:placeholder-shown`), so focus would fall to
    // <body> and the next Tab would restart at the top of the document. The
    // field is still on screen at every width now, so focus always goes back to
    // it — there is no longer a collapsing row for focus to have nowhere to go.
    this.searchInputTarget.focus()
  }

  handleSearchKeydown(event) {
    if (event.key !== "Escape" || !this.#hasSearchValue()) return

    event.preventDefault()
    this.clearSearch(event)
  }

  #handleGlobalPointerDown(event) {
    if (!this.#isPopoverOpen()) return
    if (this.filterMenuTarget.contains(event.target)) return
    if (this.filterButtonTarget.contains(event.target)) return
    this.closeFilterMenu()
  }

  #handleResize() {
    if (!this.#isPopoverOpen()) return
    this.#applyPopoverViewportSizing()
  }

  #isPopoverOpen() {
    return isPopoverOpen(this.filterMenuTarget)
  }

  #applyPopoverViewportSizing() {
    if (!this.hasFilterMenuTarget || !this.hasFilterButtonTarget) return

    const buttonRect = this.filterButtonTarget.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const spacing = 16
    const isMobile = isCompactViewport()

    if (isMobile) {
      const mobileInset = 8
      const top = Math.max(mobileInset, Math.floor(buttonRect.bottom + mobileInset))
      const maxHeight = Math.max(320, viewportHeight - top - mobileInset)
      this.filterMenuTarget.style.position = "fixed"
      this.filterMenuTarget.style.top = `${top}px`
      this.filterMenuTarget.style.left = `${mobileInset}px`
      this.filterMenuTarget.style.right = `${mobileInset}px`
      this.filterMenuTarget.style.bottom = "auto"
      this.filterMenuTarget.style.width = "auto"
      this.filterMenuTarget.style.maxHeight = `${maxHeight}px`
      this.filterMenuTarget.style.zIndex = "1004"
      this.filterMenuTarget.style.setProperty("--filter-menu-inset", `${mobileInset}px`)
      this.filterMenuTarget.style.setProperty("--filter-menu-max-height", `${maxHeight}px`)
      this.filterMenuTarget.dataset.mobileSheet = "true"
      return
    }

    const availableBelow = Math.floor(viewportHeight - buttonRect.bottom - spacing)
    const maxHeight = Math.max(320, availableBelow)
    this.#clearMobilePopoverPosition()
    this.filterMenuTarget.style.removeProperty("--filter-menu-inset")
    delete this.filterMenuTarget.dataset.mobileSheet
    this.filterMenuTarget.style.setProperty("--filter-menu-max-height", `${maxHeight}px`)
  }

  #clearMobilePopoverPosition() {
    this.filterMenuTarget.style.removeProperty("position")
    this.filterMenuTarget.style.removeProperty("top")
    this.filterMenuTarget.style.removeProperty("left")
    this.filterMenuTarget.style.removeProperty("right")
    this.filterMenuTarget.style.removeProperty("bottom")
    this.filterMenuTarget.style.removeProperty("width")
    this.filterMenuTarget.style.removeProperty("z-index")
  }

  #hasActiveFilterQueryParams() {
    const urlSearchParams = new URLSearchParams(window.location.search)
    if (urlSearchParams.has("quickFilter")) return false

    return ACTIVE_FILTER_QUERY_KEYS.some((key) => {
      return urlSearchParams.getAll(key).some((value) => value.trim() !== "")
    })
  }

  #hasActiveFilterControls() {
    if (!this.hasFilterMenuTarget) return false

    return Array.from(this.filterMenuTarget.querySelectorAll("input, select, textarea")).some((element) => {
      if (element.name === "search" || !element.name) return false
      if (element.name === "order") return false
      if (element.type === "checkbox" || element.type === "radio") return element.checked

      return element.value.trim() !== ""
    })
  }

  // The field is rendered with a nil value and marked data-turbo-permanent, so
  // on a full page load of a search URL the markup carries no value at all and
  // there is no old node for Turbo to carry one over from. Put it back from the
  // URL, which is where a shared or reloaded search link keeps it.
  #restoreSearchFromUrl() {
    if (!this.hasSearchInputTarget || this.searchInputTarget.value) return

    this.searchInputTarget.value = new URLSearchParams(window.location.search).get("search") || ""
  }

  #hasSearchValue() {
    return this.hasSearchInputTarget && this.searchInputTarget.value.trim() !== ""
  }
}
